<template>
  <div class="usage-section">
    <rest-list
      ref="list"
      :payload="fetchEndpoint"
      class="usage-table"
      @dataLoaded="onDataLoaded"
    >
      <rest-list-column
        prop="campaign_title"
        :title="$t('EmailRows_Table_LinkedCampaignName')"
        :min-width="200"
        :sortable="false"
      >
        <template v-slot="scope">
          <component
            :is="titleLinkComponent(scope.row)"
            v-if="canViewRow(scope.row)"
            v-bind="titleLinkProps(scope.row)"
            :data-qa="
              `linked-${
                scope.row.is_campaign_template
                  ? 'campaign-template'
                  : 'campaign'
              }-${scope.row.campaign_id}`
            "
          >
            {{ scope.row.campaign_title }}
          </component>
          <template v-else>
            {{ scope.row.campaign_title }}
          </template>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="is_campaign_template"
        :title="$t('EmailRows_Table_Type')"
        :min-width="100"
        :sortable="false"
        :filter-input="false"
        :filter-options="typeOptions"
        filter-field="is_campaign_template"
      >
        <template v-slot="scope">
          {{ typeLabel(scope.row) }}
        </template>
      </rest-list-column>

      <rest-list-column
        prop="email_subject"
        :title="$t('EmailRows_Table_EmailSubject')"
        :min-width="150"
        :sortable="false"
        class="email-subject-column"
      />

      <rest-list-column
        prop="variant_key"
        :title="$t('EmailRows_Table_Variant')"
        :min-width="80"
        :sortable="false"
        :filter-input="false"
        :filter-options="variantOptions"
      />

      <rest-list-column
        prop="language"
        :title="$t('EmailRows_Table_Language')"
        :min-width="100"
        :sortable="false"
        :filter-input="false"
        :filter-options="languageOptions"
      >
        <template v-slot="scope">
          {{ languageLabel(scope.row.language) }}
        </template>
      </rest-list-column>

      <rest-list-column-date
        prop="synced_at"
        :title="$t('EmailRows_Table_LastSynced')"
        :min-width="200"
        :unix="false"
        :sortable="false"
        date-format="DD/MM/YYYY HH:mm"
      />

      <rest-list-column
        prop="is_stale"
        :title="$t('EmailRows_Table_SyncStatus')"
        :min-width="100"
        :sortable="false"
        :filter-input="false"
        :filter-options="syncStatusOptions"
        filter-field="is_stale"
      >
        <template v-slot="scope">
          <el-tag
            :type="scope.row.is_stale ? 'warning' : 'success'"
            size="small"
          >
            {{
              scope.row.is_stale
                ? $t('EmailRows_Table_SyncStatus_Stale')
                : $t('EmailRows_SyncStatus_InSync')
            }}
          </el-tag>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="synced_content_version"
        :title="$t('EmailRows_Table_SyncedVersion')"
        :min-width="55"
        :sortable="false"
        :filterable="false"
      />
    </rest-list>
  </div>
</template>

<script>
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import { projectEndpoint } from '@/core/helpers'
import { languages } from '@/modules/campaign-manage/enum/LanguagesEnum'
import { campaignContentRoute } from '@/modules/email-saved-rows/helpers/campaignContentRoute'
import syncedRowTypeMixin from '@/modules/email-saved-rows/mixins/syncedRowTypeMixin'

export default {
  name: 'SyncedRowCampaignsUsageTable',
  components: {
    RestList,
    RestListColumn,
    RestListColumnDate
  },

  mixins: [syncedRowTypeMixin],

  props: {
    contentId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      loadedMessages: []
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `content/${this.contentId}/usage-messages`,
        params: {
          type: 'campaigns'
        }
      })
    },

    variantOptions() {
      const opts = {}
      this.loadedMessages.forEach(m => {
        if (m.variant_key) opts[m.variant_key] = m.variant_key
      })
      return opts
    },

    languageOptions() {
      const opts = {}
      this.loadedMessages.forEach(m => {
        if (m.language) opts[m.language] = languages[m.language] || m.language
      })
      return opts
    },

    syncStatusOptions() {
      return {
        stale: this.$t('EmailRows_Table_SyncStatus_Stale'),
        synced: this.$t('EmailRows_SyncStatus_InSync')
      }
    },

    canEdit() {
      if (this.$isRole('dpo') || this.$isRole('csa')) {
        return false
      }

      return this.$can('updateProjectItem')
    }
  },

  methods: {
    refresh() {
      this.$refs.list.refresh(true)
    },

    languageLabel(code) {
      return languages[code] || code || ''
    },

    onDataLoaded(data) {
      this.loadedMessages = data || []
    },

    titleLinkComponent(row) {
      return row.is_campaign_template ? 'a' : 'router-link'
    },

    titleLinkProps(row) {
      const route = this.editRoute(row)
      return row.is_campaign_template ? { href: route } : { to: route }
    },

    editRoute(row) {
      const path = row.is_campaign_template
        ? '/push/campaign/template'
        : '/push/campaign'
      return campaignContentRoute(
        path,
        {
          id: row.campaign_id,
          project_id: this.$route.query.project_id,
          channel: 'email',
          variant: row.variant_key,
          lang: row.language
        },
        this.canEdit
      )
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .email-subject-column .cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/modules/email-saved-rows/components/SyncedRowCampaignsUsageTable.vue